import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { MAX_UPLOAD_FILE_SIZE_LABEL } from '../../../../../constants/fileUpload';
import { Input } from '../../../../../components/ui/input';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Plus } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { DocumentTag } from '../../../types';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../redux/Project/materialSaga';
import {
  bulkCreateUpdateRegistryMiscellaneous,
  getPreSignedUrlToUploadRegistryMiscellaneous,
} from '../../../../../redux/Project/registrySaga';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { NumberInput } from '../../../../../components/ui/NumberInput';
import { refreshRegistryMiscellaneousData, fetchRegistryMiscellaneousData } from '../../../../../redux/Project/registryData/action';

interface AddMiscellaneousRegistryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  project: DashboardProject;
  itemToEdit?: any;
}

export const AddMiscellaneousRegistryModal: React.FC<AddMiscellaneousRegistryModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  project,
  itemToEdit,
}) => {
  const dispatch = useDispatch();
  const projectId = Number(project?.project?.projectId);
  const isEditMode = !!itemToEdit?.id;
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [localDocumentTags, setLocalDocumentTags] = useState<DocumentTag[]>([]);
  const [miscMaterialTypeName, setMiscMaterialTypeName] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [heatNumber, setHeatNumber] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [quantity, setQuantity] = useState<number | ''>('');
  const [sizeOrWeight, setSizeOrWeight] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState<string[]>([]);
  const [productionType, setProductionType] = useState<string>('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isAddingMiscMaterialType, setIsAddingMiscMaterialType] = useState(false);
  const [isAddingMiscLot, setIsAddingMiscLot] = useState(false);
  const [isAddingWorkOrder, setIsAddingWorkOrder] = useState(false);
  const [isAddingPO, setIsAddingPO] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [isAddingManufacturer, setIsAddingManufacturer] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[][]>([]);
  const [isAddingTag, setIsAddingTag] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const tags = await getListMaterialCategory();
        if (tags?.length) setLocalDocumentTags(tags);
      } catch (e) {
        console.error(e);
      }
    };
    if (isOpen) load();
  }, [isOpen]);

  const manufacturerOptions = useMemo(
    () =>
      localDocumentTags
        .filter(
          (t: any) =>
            t.tags_type === 'MANUFACTURER' ||
            t.tags_type === 'MANUFACTURER_TAGS' ||
            String(t.tags_type || '').toLowerCase().includes('manufacturer')
        )
        .map((t: any) => ({ value: String(t.tags_id), label: t.tags_name })),
    [localDocumentTags]
  );

  const miscMaterialTypeOptions = useMemo(
    () =>
      localDocumentTags
        .filter(
          (t: any) => t.tags_type === 'MATERIAL_MISC_MATERIAL_TYPE_TAGS' && t.project_id === projectId
        )
        .map((t: any) => ({ value: String(t.tags_id), label: t.tags_name })),
    [localDocumentTags, projectId]
  );

  const materialTypeLegacyOptions = useMemo(
    () =>
      localDocumentTags
        .filter((t: any) => t.tags_type === 'MATERIAL_TYPE_TAGS')
        .map((t: any) => ({ value: String(t.tags_id), label: t.tags_name })),
    [localDocumentTags]
  );

  const miscMaterialTypeSelectOptions = useMemo(() => {
    const merged = [...miscMaterialTypeOptions];
    const have = new Set(merged.map((o) => o.label));
    if (miscMaterialTypeName.trim() && !have.has(miscMaterialTypeName.trim())) {
      const fb = materialTypeLegacyOptions.find((o) => o.label === miscMaterialTypeName.trim());
      if (fb) merged.push(fb);
    }
    return merged;
  }, [miscMaterialTypeOptions, materialTypeLegacyOptions, miscMaterialTypeName]);

  const miscLotOptions = useMemo(
    () =>
      localDocumentTags
        .filter(
          (t: any) => t.tags_type === 'MATERIAL_MISC_LOT_NUMBER_TAGS' && t.project_id === projectId
        )
        .map((t: any) => ({ id: String(t.tags_id), name: t.tags_name })),
    [localDocumentTags, projectId]
  );

  const workOrderOptions = useMemo(
    () =>
      localDocumentTags
        .filter(
          (t: any) => t.tags_type === 'MATERIAL_WORK_ORDER_NUMBER_TAGS' && t.project_id === projectId
        )
        .map((t: any) => ({ id: String(t.tags_id), name: t.tags_name })),
    [localDocumentTags, projectId]
  );

  const poOptions = useMemo(
    () =>
      localDocumentTags
        .filter((t: any) => t.tags_type === 'MATERIAL_PO_NUMBER_TAGS' && t.project_id === projectId)
        .map((t: any) => ({ id: String(t.tags_id), name: t.tags_name })),
    [localDocumentTags, projectId]
  );

  const locationOptions = useMemo(
    () =>
      localDocumentTags
        .filter((t: any) => t.tags_type === 'MATERIAL_LOCATION_TAGS')
        .map((t: any) => ({ id: String(t.tags_id), name: t.tags_name })),
    [localDocumentTags]
  );

  const documentTags = useMemo(
    () =>
      localDocumentTags.filter(
        (t: any) => t.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS'
      ),
    [localDocumentTags]
  );

  const tagOptions = useMemo(
    () =>
      documentTags.map((tag) => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name,
      })),
    [documentTags]
  );

  const pickStr = (row: any, ...keys: string[]) => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && v !== '') return String(v);
    }
    return '';
  };

  const miscTypeCodeFromLabel = (label: string) =>
    label
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 64) || 'MISC';

  const resolveMiscMaterialTypePayload = (
    typeName: string,
    options: { value: string; label: string }[],
    tags: DocumentTag[],
    editRow: any | undefined
  ): { id: number; code: string } | undefined => {
    const trimmed = typeName.trim();
    if (!trimmed) return undefined;

    const opt = options.find((o) => o.label === trimmed);
    const idFromOpt = opt ? Number(opt.value) : NaN;
    if (Number.isFinite(idFromOpt) && idFromOpt > 0) {
      const tag = tags.find((t: any) => Number(t.tags_id) === idFromOpt);
      const code =
        (tag && (tag as any).tags_code) ||
        (tag && (tag as any).code) ||
        miscTypeCodeFromLabel(trimmed);
      return { id: idFromOpt, code: String(code) };
    }

    const embedded = editRow?.misc_material_type;
    if (embedded && typeof embedded === 'object' && embedded.id != null) {
      const id = Number(embedded.id);
      if (Number.isFinite(id) && id > 0) {
        return {
          id,
          code: String(embedded.code || '').trim() || miscTypeCodeFromLabel(trimmed),
        };
      }
    }

    const tagByName = tags.find(
      (t: any) =>
        t.tags_name === trimmed &&
        (t.tags_type === 'MATERIAL_MISC_MATERIAL_TYPE_TAGS' ||
          t.tags_type === 'MATERIAL_TYPE_TAGS')
    );
    if (tagByName?.tags_id != null) {
      const id = Number(tagByName.tags_id);
      const code =
        (tagByName as any).tags_code ||
        (tagByName as any).code ||
        miscTypeCodeFromLabel(trimmed);
      return { id, code: String(code) };
    }

    return undefined;
  };

  const collectMiscRegistryDocumentIds = (row: any): number[] => {
    const ids: number[] = [];
    const docs = row?.documents;
    if (!Array.isArray(docs)) return ids;
    for (const d of docs) {
      if (d?.materials && Array.isArray(d.materials)) {
        for (const m of d.materials) {
          const id = m.docId ?? m.doc_id ?? m.id ?? m.registryDocId;
          if (id != null && Number.isFinite(Number(id))) ids.push(Number(id));
        }
      } else {
        const id = d.docId ?? d.doc_id ?? d.id ?? d.registryDocId;
        if (id != null && Number.isFinite(Number(id))) ids.push(Number(id));
      }
    }
    return Array.from(new Set(ids));
  };

  useEffect(() => {
    if (!isOpen) return;
    if (itemToEdit) {
      setMiscMaterialTypeName(
        pickStr(itemToEdit, 'misc_material_type_name', 'miscMaterialTypeName')
      );
      setPartNumber(pickStr(itemToEdit, 'misc_part_number', 'part_number', 'partNumber'));
      setHeatNumber(pickStr(itemToEdit, 'misc_heat_number', 'heat_number', 'heatNumber'));
      setLotNumber(
        pickStr(itemToEdit, 'misc_lot_number', 'lot_number', 'lotNumber', 'misc_lot_number')
      );
      setJobNumber(
        pickStr(itemToEdit, 'misc_job_number', 'job_number', 'jobNumber', 'misc_job_number')
      );
      setPoNumber(pickStr(itemToEdit, 'misc_po_number', 'po_number', 'poNumber', 'misc_po_number'));
      const q = itemToEdit.misc_quantity ?? itemToEdit.quantity;
      setQuantity(q !== undefined && q !== null && q !== '' ? Number(q) : '');
      setSizeOrWeight(
        pickStr(
          itemToEdit,
          'misc_size_or_weight',
          'size_or_weight',
          'sizeOrWeight',
          'misc_size_or_weight'
        )
      );
      setProductionType(
        pickStr(itemToEdit, 'misc_production_type', 'production_type', 'productionType') ||
          pickStr(itemToEdit, 'misc_project_stage', 'miscProjectStage')
      );
      setLocation(pickStr(itemToEdit, 'misc_location', 'location', 'miscLocation'));
      setNotes(pickStr(itemToEdit, 'misc_notes', 'notes', 'miscNotes'));
      setMaterialDocuments([]);
      setSelectedTags([]);
      setSelectedManufacturer([]);
    } else {
      setMiscMaterialTypeName('');
      setPartNumber('');
      setHeatNumber('');
      setLotNumber('');
      setJobNumber('');
      setPoNumber('');
      setQuantity('');
      setSizeOrWeight('');
      setSelectedManufacturer([]);
      setProductionType('');
      setLocation('');
      setNotes('');
      setMaterialDocuments([]);
      setSelectedTags([]);
    }
  }, [isOpen, itemToEdit]);

  useEffect(() => {
    if (!isOpen || !itemToEdit || !manufacturerOptions.length) return;
    const extraIds = itemToEdit.misc_extra_details?.manufacturerIds;
    if (Array.isArray(extraIds) && extraIds.length > 0) {
      setSelectedManufacturer(extraIds.map((id: number | string) => String(id)));
      return;
    }
    const manu =
      pickStr(itemToEdit, 'manufacturer', 'misc_manufacturer') ||
      (itemToEdit.misc_extra_details?.manufacturer != null
        ? String(itemToEdit.misc_extra_details.manufacturer)
        : '');
    if (!manu) return;
    const names = manu.split(',').map((s: string) => s.trim()).filter(Boolean);
    const ids = names
      .map((name) => manufacturerOptions.find((o) => o.label.toLowerCase() === name.toLowerCase())?.value)
      .filter(Boolean) as string[];
    if (ids.length) setSelectedManufacturer(ids);
  }, [isOpen, itemToEdit, manufacturerOptions]);

  const handleMaterialFileSelect = (files: FileList | null) => {
    if (!files?.length) return;
    const arr = Array.from(files);
    setMaterialDocuments((prev) => [...prev, ...arr]);
    setSelectedTags((prev) => [...prev, ...Array(arr.length).fill([]).map(() => [] as string[])]);
  };

  const handleRemoveFile = (index: number) => {
    setMaterialDocuments((prev) => prev.filter((_, i) => i !== index));
    setSelectedTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    setSelectedTags((prev) => {
      const next = [...prev];
      next[fileIndex] = tags;
      return next;
    });
  };

  const handleAddDocumentTag = async (tagName: string) => {
    if (!tagName.trim() || !projectId) return;
    const existing = documentTags.find(
      (tag: any) => tag.tags_name.toLowerCase() === tagName.trim().toLowerCase()
    );
    if (existing) {
      customNotification.warning(`${tagName.trim()} tag already exists`);
      return;
    }
    const res = await AddMaterialCategory(
      {
        tags_name: tagName.trim(),
        project_id: projectId,
        tags_type: 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS',
        created_by: user.email || '',
      },
      'Document Tag'
    );
    if (res) {
      setLocalDocumentTags((prev) => [...prev, res]);
      setIsAddingTag(false);
    }
  };

  const handleAddMiscMaterialType = async (name: string) => {
    if (!name.trim() || !projectId) return;
    if (miscMaterialTypeOptions.some((o) => o.label === name.trim())) {
      customNotification.warning('Already exists');
      setIsAddingMiscMaterialType(false);
      return;
    }
    const res = await AddMaterialCategory(
      {
        tags_name: name.trim(),
        project_id: projectId,
        tags_type: 'MATERIAL_MISC_MATERIAL_TYPE_TAGS',
        created_by: user.email || 'admin',
      },
      'Misc material type'
    );
    if (res) {
      setLocalDocumentTags((prev) => [...prev, res]);
      setMiscMaterialTypeName(name.trim());
    }
    setIsAddingMiscMaterialType(false);
  };

  const addTagFlow = async (
    name: string,
    tagsType: string,
    label: string,
    onPick: (n: string) => void
  ): Promise<void> => {
    if (!name.trim() || !projectId) return;
    const res = await AddMaterialCategory(
      {
        tags_name: name.trim(),
        project_id: projectId,
        tags_type: tagsType,
        created_by: user.email || 'admin',
      },
      label
    );
    if (res) {
      setLocalDocumentTags((prev) => [...prev, res]);
      onPick(name.trim());
    }
  };

  const handleSubmit = async () => {
    if (!miscMaterialTypeName?.trim()) {
      customNotification.warning('Material type is required');
      return;
    }
    if (quantity === '' || quantity == null || !Number.isFinite(Number(quantity)) || Number(quantity) <= 0) {
      customNotification.warning('Valid quantity is required');
      return;
    }

    setIsUploading(true);
    try {
      let documents: any[] = [];
      if (materialDocuments.length > 0) {
        const s3 = await getPreSignedUrlToUploadRegistryMiscellaneous(materialDocuments, {
          projectId,
          projectTypeId: 1,
          type: 'MISCELLANEOUS_REGISTRY',
        });
        if (s3?.length) {
          documents = s3.map((file: any, index: number) => ({
            fileName: file.fileName,
            s3Key: file.s3Key,
            documentTags: selectedTags[index] || [],
            uploadedBy: user.email || '',
            docRef: index + 1,
          }));
        }
      }

      const manufacturerLabel =
        selectedManufacturer.length > 0
          ? selectedManufacturer
              .map((id) => manufacturerOptions.find((o) => o.value === id)?.label)
              .filter(Boolean)
              .join(', ')
          : null;

      const miscMaterialType = resolveMiscMaterialTypePayload(
        miscMaterialTypeName,
        miscMaterialTypeSelectOptions,
        localDocumentTags,
        itemToEdit
      );

      const existingExtra = itemToEdit?.misc_extra_details;
      const miscExtraDetails: Record<string, unknown> =
        existingExtra &&
        typeof existingExtra === 'object' &&
        !Array.isArray(existingExtra)
          ? { ...existingExtra }
          : {};
      if (manufacturerLabel) {
        miscExtraDetails.manufacturer = manufacturerLabel;
      } else {
        delete miscExtraDetails.manufacturer;
      }
      if (selectedManufacturer.length > 0) {
        miscExtraDetails.manufacturerIds = selectedManufacturer.map((id) => Number(id));
      } else {
        delete miscExtraDetails.manufacturerIds;
      }

      const documentsIds = isEditMode ? collectMiscRegistryDocumentIds(itemToEdit) : [];

      const item: Record<string, any> = {
        project_id: projectId,
        ...(miscMaterialType ? { misc_material_type: miscMaterialType } : {}),
        misc_material_type_name: miscMaterialTypeName.trim(),
        misc_part_number: partNumber.trim() || null,
        misc_heat_number: heatNumber.trim() || null,
        misc_lot_number: lotNumber.trim() || null,
        misc_job_number: jobNumber.trim() || null,
        misc_po_number: poNumber.trim() || null,
        misc_quantity: Number(quantity),
        misc_size_or_weight: sizeOrWeight.trim() || null,
        misc_production_type: productionType?.trim() ? productionType.trim() : null,
        misc_location: location.trim() || null,
        misc_notes: notes.trim() || null,
        misc_extra_details:
          Object.keys(miscExtraDetails).length > 0 ? miscExtraDetails : {},
        documents_ids: documentsIds,
      };

      if (documents.length > 0) {
        item.documents = documents;
      }

      if (isEditMode && itemToEdit.id) {
        item.id = itemToEdit.id;
      }

      const result = await bulkCreateUpdateRegistryMiscellaneous({ items: [item] });
      if (result) {
        if (isEditMode) {
          customNotification.success(
            'Miscellaneous item updated',
            'The miscellaneous registry item has been updated successfully.'
          );
        } else {
          customNotification.success(
            'Miscellaneous item added',
            'The miscellaneous registry item has been added successfully.'
          );
        }
        dispatch(refreshRegistryMiscellaneousData({ projectId }));
        dispatch(fetchRegistryMiscellaneousData({ projectId }));
        onSaved();
        handleClose();
      }
    } catch {
      customNotification.error(
        'Save failed',
        'There was an error saving the miscellaneous registry item. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddManufacturer = async (manufacturerName: string) => {
    if (!manufacturerName.trim() || !projectId) return;
    if (
      manufacturerOptions.some(
        (opt) => opt.label.toLowerCase() === manufacturerName.trim().toLowerCase()
      )
    ) {
      customNotification.warning(`${manufacturerName.trim()} manufacturer already exists`);
      return;
    }
    const res = await AddMaterialCategory(
      {
        tags_name: manufacturerName.trim(),
        project_id: projectId,
        tags_type: 'MANUFACTURER',
        created_by: user.email || '',
      },
      'Manufacturer'
    );
    if (res) {
      setLocalDocumentTags((prev) => [...prev, res]);
      setIsAddingManufacturer(false);
      setSelectedManufacturer([String(res.tags_id ?? '')]);
    }
  };

  const handleClose = () => {
    setMiscMaterialTypeName('');
    setPartNumber('');
    setHeatNumber('');
    setLotNumber('');
    setJobNumber('');
    setPoNumber('');
    setQuantity('');
    setSizeOrWeight('');
    setSelectedManufacturer([]);
    setProductionType('');
    setLocation('');
    setNotes('');
    setMaterialDocuments([]);
    setSelectedTags([]);
    setIsUploading(false);
    onClose();
  };

  const isSubmitDisabled =
    isUploading ||
    !miscMaterialTypeName?.trim() ||
    quantity === '' ||
    quantity == null ||
    !Number.isFinite(Number(quantity)) ||
    Number(quantity) <= 0;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="w-[90vw] max-w-[1200px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              {isEditMode ? 'Edit' : 'Add'} Miscellaneous Registry Item
            </DialogTitle>
          </DialogHeader>

          <div className="grid p-2 grid-cols-4 gap-4 min-w-0 max-h-[75vh] flex flex-col overflow-y-auto overflow-x-hidden">
            <div className="space-y-2 col-span-4">
              <Label>
                Material type <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                value={miscMaterialTypeName}
                onValueChange={(value: string) => setMiscMaterialTypeName(value)}
                placeholder="Select material type"
                searchPlaceholder="Search material types..."
                options={miscMaterialTypeSelectOptions.map((t) => ({ id: t.value, name: t.label }))}
                onAddNew={() => setIsAddingMiscMaterialType(true)}
                addNewText="Add New Misc Material Type"
                showAddButton
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="miscPartNumber">Part number</Label>
              <Input
                id="miscPartNumber"
                value={partNumber}
                onChange={(e) => setPartNumber(e.target.value)}
                placeholder="Enter part number"
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="miscHeatNumber">Heat number</Label>
              <Input
                id="miscHeatNumber"
                value={heatNumber}
                onChange={(e) => setHeatNumber(e.target.value)}
                placeholder="Enter heat number"
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label>Lot number</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={lotNumber}
                  onValueChange={setLotNumber}
                  placeholder="Select lot number"
                  searchPlaceholder="Search lot numbers..."
                  options={miscLotOptions}
                  onAddNew={() => setIsAddingMiscLot(true)}
                  addNewText="Add New Lot Number"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Job number</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={jobNumber}
                  onValueChange={setJobNumber}
                  placeholder="Select job number"
                  searchPlaceholder="Search job numbers..."
                  options={workOrderOptions}
                  onAddNew={() => setIsAddingWorkOrder(true)}
                  addNewText="Add New Job Number"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>PO number</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={poNumber}
                  onValueChange={setPoNumber}
                  placeholder="Select PO number"
                  searchPlaceholder="Search PO numbers..."
                  options={poOptions}
                  onAddNew={() => setIsAddingPO(true)}
                  addNewText="Add New PO Number"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="miscQuantity">
                Quantity <span className="text-red-500">*</span>
              </Label>
              <NumberInput
                id="miscQuantity"
                value={quantity}
                onChange={(v) => setQuantity(v !== '' && v != null ? Number(v) : '')}
                placeholder="Enter quantity"
                className="w-full"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="miscSizeOrWeight">Size / weight</Label>
              <Input
                id="miscSizeOrWeight"
                value={sizeOrWeight}
                onChange={(e) => setSizeOrWeight(e.target.value)}
                placeholder="e.g. 10 ft, 25 kg"
                className="w-full"
              />
            </div>

            <div className="space-y-2 col-span-4">
              <Label>Manufacturer</Label>
              <MultiSelect
                options={manufacturerOptions}
                selected={selectedManufacturer}
                onChange={setSelectedManufacturer}
                placeholder="Select manufacturer"
                isSearchable
                className="w-full"
                showAddButton
                addButtonText="Add New Manufacturer"
                onAddButtonClick={() => setIsAddingManufacturer(true)}
                maxHeight="200px"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Production type</Label>
              <Select value={productionType || undefined} onValueChange={setProductionType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select production type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Welding">Welding</SelectItem>
                  <SelectItem value="Coating">Coating</SelectItem>
                  <SelectItem value="Fabrication">Fabrication</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="miscLocation">Location</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={location}
                  onValueChange={setLocation}
                  placeholder="Select location"
                  searchPlaceholder="Search locations..."
                  options={locationOptions}
                  onAddNew={() => setIsAddingLocation(true)}
                  addNewText="Add New Location"
                />
              </div>
            </div>

            <div className="space-y-2 col-span-4">
              <Label className="text-base font-medium">Note</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:border-blue-400"
                placeholder="Enter notes..."
              />
            </div>

            <div className="space-y-2 col-span-4">
              <Label>Upload Documents</Label>
              <p className="text-sm text-gray-600">
                Upload miscellaneous registry documents.
                <br />
                <span className="text-xs text-gray-500">
                  Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
                  <br />
                  Maximum file size: {MAX_UPLOAD_FILE_SIZE_LABEL} per file
                </span>
              </p>

              <FileUpload
                onFileSelect={handleMaterialFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                multiple={true}
                maxFiles={100}
                buttonText="Upload Documents"
                variant="outline"
                size="lg"
                onRemoveFile={handleRemoveFile}
                selectedFiles={materialDocuments}
                showFileList={materialDocuments.length > 0}
                className="w-full"
                showTags={true}
                optionsTags={tagOptions}
                selectedTags={selectedTags}
                showAddButton={true}
                addButtonText="Add New Tag"
                onAddButtonClick={() => setIsAddingTag(true)}
                onTagsChange={handleTagsChange}
                maxHeight=""
              />

              <div className="text-xs text-gray-500">
                Current files: {materialDocuments.length} file(s)
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex items-center gap-2"
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              className="flex items-center gap-2 bg-[#e04530] text-white hover:bg-[#e04530]/90"
            >
              <Plus className="h-4 w-4" />
              {isUploading ? 'Saving...' : isEditMode ? 'Update Item' : 'Save Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddNewItemDialog
        open={isAddingMiscMaterialType}
        onOpenChange={setIsAddingMiscMaterialType}
        title="Add misc material type"
        label="Type name"
        placeholder="Enter material type name"
        onAdd={handleAddMiscMaterialType}
        buttonText="Add type"
        cancelText="Cancel"
      />
      <AddNewItemDialog
        open={isAddingMiscLot}
        onOpenChange={setIsAddingMiscLot}
        title="Add lot number"
        label="Lot number"
        placeholder="Enter lot number"
        onAdd={async (n) => {
          await addTagFlow(n, 'MATERIAL_MISC_LOT_NUMBER_TAGS', 'Lot number', setLotNumber);
        }}
        buttonText="Add lot number"
        cancelText="Cancel"
      />
      <AddNewItemDialog
        open={isAddingWorkOrder}
        onOpenChange={setIsAddingWorkOrder}
        title="Add job number"
        label="Job number"
        placeholder="Enter job number"
        onAdd={async (n) => {
          await addTagFlow(n, 'MATERIAL_WORK_ORDER_NUMBER_TAGS', 'Job number', setJobNumber);
        }}
        buttonText="Add job number"
        cancelText="Cancel"
      />
      <AddNewItemDialog
        open={isAddingPO}
        onOpenChange={setIsAddingPO}
        title="Add PO number"
        label="PO number"
        placeholder="Enter PO number"
        onAdd={async (n) => {
          await addTagFlow(n, 'MATERIAL_PO_NUMBER_TAGS', 'PO number', setPoNumber);
        }}
        buttonText="Add PO number"
        cancelText="Cancel"
      />
      <AddNewItemDialog
        open={isAddingLocation}
        onOpenChange={setIsAddingLocation}
        title="Add location"
        label="Location name"
        placeholder="Enter location name"
        onAdd={async (n) => {
          await addTagFlow(n, 'MATERIAL_LOCATION_TAGS', 'Location', setLocation);
        }}
        buttonText="Add location"
        cancelText="Cancel"
      />

      <AddNewItemDialog
        open={isAddingManufacturer}
        onOpenChange={setIsAddingManufacturer}
        title="Add New Manufacturer"
        label="Manufacturer Name"
        placeholder="Enter manufacturer name"
        onAdd={handleAddManufacturer}
        buttonText="Add Manufacturer"
        cancelText="Cancel"
      />

      <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddDocumentTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />
    </>
  );
};
